import { Compass, Activity, HelpCircle, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";
import InfoTip from "./InfoTip";
import type { ParentAnalyticsResult } from "@/hooks/useParentAnalytics";
import type { ScorecardRow, CommandStatus } from "@/lib/parent/commandMetrics";

interface Props {
  analytics: ParentAnalyticsResult;
  scorecard: ScorecardRow[];
  status: CommandStatus;
}

export default function RiskOpportunityPanel({ analytics, scorecard, status }: Props) {
  const activeDays = activeDaysThisWeek(analytics);
  const engagementSignal =
    status.state === "reengage" ? "Needs attention" : activeDays === 0 ? "Quiet" : "Active";
  const needsQuiz = scorecard.filter((s) => s.signal === "Needs Quiz").length;

  const tiles = [
    {
      icon: Activity,
      label: "How active this week",
      badge: engagementSignal,
      badgeTone:
        engagementSignal === "Needs attention"
          ? "text-amber-200 border-amber-400/30 bg-amber-500/10"
          : engagementSignal === "Quiet"
            ? "text-sky-200 border-sky-400/30 bg-sky-500/10"
            : "text-emerald-200 border-emerald-400/30 bg-emerald-500/10",
      detail: activeDays === 0 ? "0 active days this week" : `${activeDays} active day${activeDays === 1 ? "" : "s"} this week`,
      tip: {
        title: "How active this week",
        body: "Counts the days your learner did at least one lesson or quiz this week. A quiet week is just a nudge to plan a short session together.",
        nextStep: engagementSignal === "Needs attention" ? "Plan a short 15-minute session tonight." : undefined,
      },
      iconColor: "text-amber-200",
    },
    {
      icon: HelpCircle,
      label: "Areas needing a quiz",
      badge: needsQuiz > 0 ? "Try a quiz" : "Coverage OK",
      badgeTone: needsQuiz > 0 ? "text-amber-200 border-amber-400/30 bg-amber-500/10" : "text-emerald-200 border-emerald-400/30 bg-emerald-500/10",
      detail: needsQuiz > 0 ? `${needsQuiz} area${needsQuiz === 1 ? "" : "s"} need assessment` : "All tracked areas have quiz data",
      tip: {
        title: "Areas needing a quiz",
        body: "These topics have lesson activity but no quiz attempts yet — we can't confidently call them strengths without one.",
        nextStep: needsQuiz > 0 ? "Complete one quiz in each area to sharpen the readiness signal." : undefined,
      },
      iconColor: "text-amber-300",
    },
    {
      icon: Sparkles,
      label: "Smallest next step",
      badge: status.state === "reengage" ? "15-min session" : status.state === "empty" ? "Start lesson" : "Keep cadence",
      badgeTone: "text-emerald-200 border-emerald-400/30 bg-emerald-500/10",
      detail: status.state === "reengage" ? "Quick win to rebuild momentum" : status.state === "empty" ? "Open the first lesson together" : "Pick the next lesson together",
      tip: {
        title: "Smallest next step",
        body: "The lightest helpful action this week — designed to keep momentum without pressure.",
      },
      iconColor: "text-emerald-300",
    },
  ];

  return (
    <section className="relative h-full overflow-hidden rounded-3xl border border-white/[0.06] bg-gradient-to-br from-card/80 via-card/60 to-card/40 p-5 backdrop-blur-2xl shadow-[0_24px_60px_-30px_hsl(var(--primary)/0.45),inset_0_1px_0_0_hsl(var(--foreground)/0.05)]">
      <div className="relative">
        <div className="flex items-center gap-2">
          <span className="flex h-7 w-7 items-center justify-center rounded-xl bg-primary/10 text-primary ring-1 ring-primary/20">
            <Compass className="h-3.5 w-3.5" />
          </span>
          <h3 className="font-heading text-base font-semibold tracking-tight text-foreground sm:text-lg">Where to focus</h3>
        </div>
        <p className="mt-1.5 text-[11px] text-muted-foreground">Calm cues so you know where to lean in this week.</p>

        <div className="mt-4 space-y-2">
          {tiles.map((t) => (
            <div key={t.label} className="flex items-start gap-3 rounded-2xl border border-white/[0.05] bg-background/30 p-3">
              <span className={cn("flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-background/40 ring-1 ring-white/10", t.iconColor)}>
                <t.icon className="h-4 w-4" />
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm font-semibold text-foreground">{t.label}</p>
                  <span className={cn("rounded-full border px-2 py-0.5 text-[10px] font-medium", t.badgeTone)}>{t.badge}</span>
                </div>
                <p className="mt-0.5 text-[11px] text-muted-foreground">{t.detail}</p>
              </div>
              <InfoTip {...t.tip} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function activeDaysThisWeek(a: ParentAnalyticsResult): number {
  const now = new Date();
  const day = (now.getDay() + 6) % 7;
  const monday = new Date(now);
  monday.setHours(0, 0, 0, 0);
  monday.setDate(monday.getDate() - day);
  const days = new Set<string>();
  for (const ev of a.lessonActivity) {
    const d = new Date(ev.date);
    if (d >= monday) days.add(d.toDateString());
  }
  for (const ev of a.quizAttempts) {
    const d = new Date(ev.completedAt);
    if (d >= monday) days.add(d.toDateString());
  }
  return days.size;
}